import { useTranslation } from 'react-i18next';
import { FeatureToggle } from '@/common/api/datasources/Api';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Calendar, GripVertical, Loader2, Info } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import React, { forwardRef } from 'react';
import { UserTooltip } from '@/components/shared/UserTooltip/UserTooltip';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface FeatureToggleItemProps {
    toggle: FeatureToggle;
    onToggle: (toggle: FeatureToggle) => void;
    isUpdating: boolean | ((key: string) => boolean);
    isReordering?: boolean | ((key: string) => boolean);
    isDraggable?: boolean;
    className?: string;
    dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const FeatureToggleItem = forwardRef<HTMLDivElement, FeatureToggleItemProps>(({
    toggle,
    onToggle,
    isUpdating,
    isReordering,
    isDraggable,
    className,
    dragHandleProps,
    ...props
}, ref) => {
    const { t } = useTranslation();

    // Форматуємо дату оновлення
    const formattedDate = toggle.updatedAt
        ? formatDistanceToNow(new Date(toggle.updatedAt), { addSuffix: true })
        : null;

    // Перевіряємо, чи цей конкретний toggle оновлюється зараз
    const isThisUpdating = typeof isUpdating === 'function'
        ? isUpdating(toggle.key)
        : isUpdating;

    // Перевіряємо, чи цей конкретний toggle змінює порядок зараз
    const isThisReordering = typeof isReordering === 'function'
        ? isReordering(toggle.key)
        : isReordering || false;

    // Обробник перемикання toggle
    const handleToggleChange = () => {
        if (!isThisUpdating && !isThisReordering) {
            onToggle(toggle);
        }
    };

    // Функція для перевірки валідності UUID
    const isValidUUID = (uuid: string): boolean => {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(uuid);
    };

    return (
        <Card
            ref={ref}
            className={cn(
                "transition-all duration-200 border hover:shadow-md",
                toggle.enabled
                    ? "bg-card border-l-4 border-l-primary"
                    : "bg-muted/20 border-l-4 border-l-muted",
                className
            )}
            {...props}
        >
            <CardHeader className="px-4 py-3 flex flex-row items-start justify-between gap-2">
                <div className="flex items-center gap-2 w-full">
                    {isDraggable && (
                        <div
                            {...dragHandleProps}
                            className="cursor-grab active:cursor-grabbing p-1 rounded-md hover:bg-muted transition-colors duration-200 self-start mt-1"
                            title={t('systemSettings.featureToggles.dragToReorder')}
                        >
                            <GripVertical className="h-5 w-5 text-muted-foreground" />
                        </div>
                    )}

                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col">

                            <p className={cn(
                                "text-base mb-3 leading-snug",
                                toggle.enabled ? "text-foreground" : "text-muted-foreground"
                            )}>
                                {toggle.description || (
                                    <span className="text-muted italic">
                                        {t('systemSettings.featureToggles.noDescription')}
                                    </span>
                                )}
                            </p>

                            <div className="text-sm text-muted-foreground font-mono group">
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="truncate max-w-[200px] inline-block align-bottom group-hover:text-primary transition-colors duration-200">
                                                {toggle.key}
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>{toggle.key}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>


                        </div>
                    </div>

                    <div className="flex flex-shrink-0 items-center">
                        <div className="flex items-center h-full">
                            {isThisUpdating && (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            )}
                            <Switch
                                checked={toggle.enabled}
                                onCheckedChange={handleToggleChange}
                                disabled={isThisUpdating || isThisReordering}
                                aria-label={t(
                                    toggle.enabled
                                        ? 'systemSettings.featureToggles.disable'
                                        : 'systemSettings.featureToggles.enable',
                                    { key: toggle.key }
                                )}
                            />
                        </div>
                    </div>
                </div>
            </CardHeader>

            <CardContent className="px-4 pb-3 pt-0">
                <div className="flex items-center justify-between border-t border-border pt-2 mt-1 text-xs text-muted-foreground">
                    {formattedDate && (
                        <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>{formattedDate}</span>
                        </div>
                    )}
                    {toggle.updatedBy && !(!toggle.updatedBy ||
                        toggle.updatedBy === 'undefined' ||
                        toggle.updatedBy === 'null' ||
                        !isValidUUID(toggle.updatedBy)) && (
                            <UserTooltip
                                userUUID={toggle.updatedBy}
                                size="xs"
                                showDetails={true}
                            />
                        )}
                </div>
            </CardContent>
        </Card>
    );
}); 